import { Flex, Text } from "@chakra-ui/react";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useRecoilValue } from "recoil";
import * as ChannelService from "@channel.io/channel-web-sdk-loader";
import {
  channelTalkBootedState,
  channelTalkMessengerOpenState,
  channelTalkUnreadState,
} from "@/shared/store/channelTalkStore";
import { pwaBannerHeightState } from "@/shared/store/pwaBannerStore";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import useOpenChannelTalk from "@/shared/hooks/useOpenChannelTalk";

// 채널톡 기본 런처와 같은 크기·여백.
const SIZE = 54;
const DEFAULT_MARGIN = 24;
// 드래그로 화면 밖까지 나가지 않도록 남기는 여백.
const EDGE_MARGIN = 8;
// 이만큼 움직이면 클릭이 아니라 드래그로 본다.
const DRAG_THRESHOLD = 4;
// 위치는 사람마다 다른 취향이라 브라우저에 남긴다(다음 방문에도 그 자리).
const STORAGE_KEY = "pathrade.channelTalk.launcherPosition";

// 채널톡 기본 런처를 가리는 스타일. 래퍼(data-ch-testid="launcher") 안에 버튼과
// 메시지 미리보기 말풍선이 함께 들어 있고, #ch-plugin-launcher 는 커스텀 이미지 런처의 버튼이다.
// 클래스명은 빌드마다 바뀌는 해시라 쓰지 않는다.
const HIDE_STYLE_ID = "pathrade-ch-launcher-hide";
const HIDE_STYLE_CSS =
  '[data-ch-testid="launcher"],#ch-plugin-launcher{display:none!important;}';

// 채널톡은 UI 를 #ch-plugin-entry 아래 shadow root(mode: "open") 안에 그린다.
// 페이지의 CSS 는 shadow 경계를 넘지 못해서, 스타일을 그 안에 직접 넣어야 한다.
function findChannelShadowRoot(): ShadowRoot | null {
  const entry = document.getElementById("ch-plugin-entry");
  if (!entry) return null;
  const host = Array.from(entry.querySelectorAll<HTMLElement>("*")).find(
    (element) => element.shadowRoot
  );
  return host?.shadowRoot ?? null;
}

interface Position {
  x: number;
  y: number;
}

// bottomInset 은 PWA 설치 안내 배너 높이. 기본 위치가 화면 최하단이라 인셋을
// 빼주지 않으면 배너와 정면으로 겹친다.
function clampToViewport({ x, y }: Position, bottomInset = 0): Position {
  const maxX = Math.max(EDGE_MARGIN, window.innerWidth - SIZE - EDGE_MARGIN);
  const maxY = Math.max(
    EDGE_MARGIN,
    window.innerHeight - SIZE - EDGE_MARGIN - bottomInset
  );
  return {
    x: Math.min(Math.max(x, EDGE_MARGIN), maxX),
    y: Math.min(Math.max(y, EDGE_MARGIN), maxY),
  };
}

function getDefaultPosition(bottomInset = 0): Position {
  return clampToViewport(
    {
      x: window.innerWidth - SIZE - DEFAULT_MARGIN,
      y: window.innerHeight - SIZE - DEFAULT_MARGIN,
    },
    bottomInset
  );
}

function readStoredPosition(): Position | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (typeof parsed?.x === "number" && typeof parsed?.y === "number") {
      return { x: parsed.x, y: parsed.y };
    }
  } catch {
    // 시크릿 모드 등 localStorage 가 막혀 있으면 기본 위치로 시작한다.
  }
  return null;
}

function writeStoredPosition(position: Position) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(position));
  } catch {
    // 저장에 실패해도 이번 세션에서의 이동 자체는 동작해야 한다.
  }
}

// 채널톡 런처와 같은 말풍선 모양.
function ChatBubbleIcon() {
  return (
    <svg width={26} height={26} viewBox="0 0 24 24" fill="none">
      <path
        d="M12 2.75c-5.108 0-9.25 3.61-9.25 8.063 0 2.57 1.38 4.86 3.535 6.34a.75.75 0 0 1 .32.7l-.23 2.06a.6.6 0 0 0 .88.6l2.62-1.43a.75.75 0 0 1 .5-.07c.53.09 1.07.14 1.625.14 5.108 0 9.25-3.61 9.25-8.34S17.108 2.75 12 2.75Z"
        fill="currentColor"
      />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg width={22} height={22} viewBox="0 0 24 24" fill="none">
      <path
        d="M6 6l12 12M18 6L6 18"
        stroke="currentColor"
        strokeWidth={2.2}
        strokeLinecap="round"
      />
    </svg>
  );
}

/**
 * 자체 채널톡 런처.
 *
 * 채널톡 기본 플로팅 버튼은 boot 시 감추고(hideChannelButtonOnBoot) 이 버튼만 쓴다.
 * 기본 버튼은 채널톡 소유의 DOM 이라 위치를 옮길 수 없어서, 같은 모양으로 직접 그려
 * 드래그로 자유롭게 옮길 수 있게 했다.
 *
 * 열림/닫힘 상태는 SDK 콜백(onShowMessenger/onHideMessenger)이 갱신하므로,
 * 상담창 안의 닫기 버튼이나 다른 화면의 showMessenger() 로 열고 닫아도 아이콘이 따라간다.
 */
export default function ChannelTalkLauncher() {
  const { t } = useI18nHelper();
  // 열림/닫힘은 SDK 콜백만 갱신한다. 미리 바꿔두면 실제로 안 열렸을 때
  // 아이콘만 X 가 되어 다음 클릭이 '닫기'로 나가버린다.
  const isMessengerOpen = useRecoilValue(channelTalkMessengerOpenState);
  const isBooted = useRecoilValue(channelTalkBootedState);
  const unread = useRecoilValue(channelTalkUnreadState);
  const bannerHeight = useRecoilValue(pwaBannerHeightState);
  const openChannelTalk = useOpenChannelTalk();
  const hideStyleRef = useRef<HTMLStyleElement | null>(null);

  // 위치는 화면 크기를 알아야 정해져서 마운트 이후에 채운다(SSR 대비).
  const [position, setPosition] = useState<Position | null>(null);
  const dragRef = useRef({
    isDragging: false,
    hasMoved: false,
    startX: 0,
    startY: 0,
    offsetX: 0,
    offsetY: 0,
  });

  // 드래그·리사이즈 핸들러는 의존성 배열이 비어 있어야 재생성되지 않는다.
  // 배너 높이는 렌더마다 바뀔 수 있으니 ref 로 최신값만 넘긴다.
  const bannerHeightRef = useRef(bannerHeight);
  bannerHeightRef.current = bannerHeight;

  useEffect(() => {
    setPosition(
      readStoredPosition() ?? getDefaultPosition(bannerHeightRef.current)
    );
  }, []);

  // 배너가 뜨거나 사라지면 겹치지 않는 자리로 다시 끌어온다.
  // localStorage 에 하단 좌표가 남아 있는 기존 사용자에게도 이 경로가 필요하다.
  useEffect(() => {
    setPosition((prev) => (prev ? clampToViewport(prev, bannerHeight) : prev));
  }, [bannerHeight]);

  // 채널톡 기본 런처 숨기기.
  // hideChannelButton() 만으로는 부족하다 — 상담원 답변이 오면 채널톡이 푸시 팝업을 띄우면서
  // 숨김 여부와 무관하게 런처 영역을 다시 보여준다. 그래서 shadow root 안에 스타일을 넣어 가린다.
  useEffect(() => {
    const ensureHidden = () => {
      // 앱 전체 DOM 변경마다 불리므로 이미 넣어둔 경우는 즉시 빠진다.
      // (boot 이 다시 돌면 shadow root 째로 새로 그려져 style 이 문서에서 떨어진다)
      if (hideStyleRef.current?.isConnected) return;

      const root = findChannelShadowRoot();
      if (!root) return;

      const style = document.createElement("style");
      style.id = HIDE_STYLE_ID;
      style.textContent = HIDE_STYLE_CSS;
      root.appendChild(style);
      hideStyleRef.current = style;
    };

    ensureHidden();

    // shadow root 는 boot 이후에 생기고, 재부팅 시 다시 만들어진다. 계속 지켜본다.
    // 채널톡 컨테이너(#ch-plugin)가 이미 있으면 그 안만 본다 — 앱 전체를 보면 낭비다.
    const observer = new MutationObserver(ensureHidden);
    observer.observe(document.getElementById("ch-plugin") ?? document.body, {
      childList: true,
      subtree: true,
    });
    return () => observer.disconnect();
  }, []);

  // 창 크기가 줄어 버튼이 화면 밖으로 나가면 다시 안으로 끌어온다.
  useEffect(() => {
    const handleResize = () => {
      setPosition((prev) =>
        prev ? clampToViewport(prev, bannerHeightRef.current) : prev
      );
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handlePointerDown = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (!position) return;
      event.currentTarget.setPointerCapture(event.pointerId);
      dragRef.current = {
        isDragging: true,
        hasMoved: false,
        startX: event.clientX,
        startY: event.clientY,
        offsetX: event.clientX - position.x,
        offsetY: event.clientY - position.y,
      };
    },
    [position]
  );

  const handlePointerMove = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      const drag = dragRef.current;
      if (!drag.isDragging) return;

      if (
        Math.abs(event.clientX - drag.startX) > DRAG_THRESHOLD ||
        Math.abs(event.clientY - drag.startY) > DRAG_THRESHOLD
      ) {
        drag.hasMoved = true;
      }

      setPosition(
        clampToViewport(
          {
            x: event.clientX - drag.offsetX,
            y: event.clientY - drag.offsetY,
          },
          bannerHeightRef.current
        )
      );
    },
    []
  );

  const handlePointerUp = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      const drag = dragRef.current;
      if (!drag.isDragging) return;
      drag.isDragging = false;
      event.currentTarget.releasePointerCapture(event.pointerId);

      // 옮긴 것이면 위치만 기억한다. 여닫기는 이어서 오는 click 이 처리한다.
      if (drag.hasMoved) {
        setPosition((prev) => {
          if (prev) writeStoredPosition(prev);
          return prev;
        });
      }
    },
    []
  );

  // 토글은 pointerup 이 아니라 click 에서 한다 — 드래그 도중 pointercancel 이 끼면
  // pointerup 이 오지 않아 클릭이 통째로 씹힌다.
  const handleClick = useCallback(() => {
    if (dragRef.current.hasMoved) {
      dragRef.current.hasMoved = false;
      return;
    }

    // 열기는 boot 전(로그인 직후 /users/me 응답 대기 등)이면 예약된다.
    if (isMessengerOpen) {
      ChannelService.hideMessenger();
    } else {
      openChannelTalk();
    }
  }, [isMessengerOpen, openChannelTalk]);

  if (!position) return null;

  return (
    <Flex
      position={"fixed"}
      left={`${position.x}px`}
      top={`${position.y}px`}
      width={`${SIZE}px`}
      height={`${SIZE}px`}
      borderRadius={"50%"}
      background={"#1272EF"}
      color={"#FFFFFF"}
      alignItems={"center"}
      justifyContent={"center"}
      boxShadow={"0 4px 12px rgba(0, 0, 0, 0.25)"}
      cursor={isBooted ? "pointer" : "progress"}
      opacity={isBooted ? 1 : 0.6}
      // 상담창(메신저)이 아주 높은 z-index 를 쓰므로 그 위에 얹어야 X 를 누를 수 있다.
      zIndex={10000001}
      // 모바일에서 드래그가 페이지 스크롤로 먹히지 않도록.
      sx={{ touchAction: "none" }}
      userSelect={"none"}
      _hover={{ background: "#005AAD" }}
      role={"button"}
      aria-label={t(
        isMessengerOpen
          ? "common:channel_talk.close_messenger"
          : "common:channel_talk.open_messenger"
      )}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={() => {
        dragRef.current.isDragging = false;
      }}
      onClick={handleClick}
    >
      {isMessengerOpen ? <CloseIcon /> : <ChatBubbleIcon />}
      {/* 기본 런처의 안 읽은 표시를 대신한다. 상담창이 열려 있으면 볼 필요가 없다. */}
      {!isMessengerOpen && unread > 0 && (
        <Flex
          position={"absolute"}
          top={"0"}
          right={"0"}
          minWidth={"20px"}
          height={"20px"}
          padding={"0 6px"}
          borderRadius={"10px"}
          background={"#EE4B2B"}
          alignItems={"center"}
          justifyContent={"center"}
        >
          <Text fontSize={"11px"} fontWeight={"700"} color={"#FFFFFF"}>
            {unread > 99 ? "99+" : unread}
          </Text>
        </Flex>
      )}
    </Flex>
  );
}
